/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { BinaryFileListResource, BinaryFileService } from '@common/binary-file-shared';
import {
  CommandBasedService,
  CommandListResourceServiceConfig,
  CommandOrder,
  CommandResource,
  tapOnCommandSuccessfullyDone,
} from '@alfa-client/command-shared';
import {
  AdditionalFeatureKey,
  createEmptyStateResource,
  EMPTY_STRING,
  isNotEmpty,
  ResourceServiceConfig,
  StateResource,
} from '@common/tech-shared';
import { VorgangHeaderLinkRel, VorgangService, VorgangWithEingangResource } from '@alfa-client/vorgang-shared';
import { inject, Injectable } from '@angular/core';
import { getUrl, hasLink, ResourceUri } from '@ngxp/rest';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { kommentarReducer } from './+state/kommentar.reducer';
import { KommentarLinkRel, KommentarListLinkRel } from './kommentar.linkrel';
import { Kommentar, KOMMENTAR_UPLOADED_ATTACHMENTS, KommentarListResource, KommentarResource } from './kommentar.model';

@Injectable()
export class KommentarService extends CommandBasedService<
  KommentarResource,
  KommentarResource,
  VorgangWithEingangResource,
  KommentarListResource,
  KommentarResource,
  Kommentar
> {
  private readonly binaryFileService = inject(BinaryFileService);

  readonly formularVisibility$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly _currentlyEdited$: BehaviorSubject<ResourceUri> = new BehaviorSubject(EMPTY_STRING);

  getListResourceConfig(): CommandListResourceServiceConfig<VorgangWithEingangResource> {
    const vorgangService = inject(VorgangService);
    return {
      baseResource: vorgangService.selectVorgangWithEingang(),
      getLinkRel: VorgangHeaderLinkRel.KOMMENTARE,
      listResourceListLinkRel: KommentarListLinkRel.KOMMENTAR_LIST,
      create: {
        linkRel: KommentarListLinkRel.CREATE_KOMMENTAR,
        order: CommandOrder.CREATE_KOMMENTAR,
      },
    };
  }

  getResourceConfig(): ResourceServiceConfig<KommentarResource> {
    return {
      baseResource: this._getListResourceService().getSelected(),
      getLinkRel: KommentarLinkRel.SELF,
      edit: { linkRel: KommentarLinkRel.EDIT, order: CommandOrder.EDIT_KOMMENTAR },
    };
  }

  getFeatureName(): string {
    return 'Kommentar';
  }

  override getAdditionalFeatures(listResourcePath: string): AdditionalFeatureKey[] {
    return [{ key: listResourcePath, reducerFn: kommentarReducer }];
  }

  public isNewKommentarFormularVisible(): Observable<boolean> {
    return combineLatest([this.formularVisibility$, this._currentlyEdited$]).pipe(
      map(([isVisible, currentlyEdited]) => isVisible && currentlyEdited === EMPTY_STRING),
    );
  }

  public showNewKommentarFormular(): void {
    this.setCurrentlyEdited(EMPTY_STRING);
    this.formularVisibility$.next(true);
  }

  public hideNewKommentarFormular(): void {
    this.formularVisibility$.next(false);
  }

  public createKommentar(toCreateKommentar: Kommentar): Observable<StateResource<CommandResource>> {
    return this.create(toCreateKommentar).pipe(tapOnCommandSuccessfullyDone(() => this._afterCreateOrEditKommentarDone()));
  }

  public editKommentar(toEditKommentar: Kommentar): Observable<StateResource<CommandResource>> {
    return this.save(toEditKommentar).pipe(tapOnCommandSuccessfullyDone(() => this._afterCreateOrEditKommentarDone()));
  }

  _afterCreateOrEditKommentarDone(): void {
    this.hideNewKommentarFormular();
    this.markListAsReload();
  }

  _getAttachments(kommentar: KommentarResource): Observable<StateResource<BinaryFileListResource>> {
    if (hasLink(kommentar, KommentarLinkRel.ATTACHMENTS)) {
      return this.binaryFileService.getFiles(kommentar, KommentarLinkRel.ATTACHMENTS);
    }
    return of(createEmptyStateResource<BinaryFileListResource>());
  }

  public selectKommentar(kommentar: KommentarResource): void {
    this._getListResourceService().select(getUrl(kommentar, KommentarLinkRel.SELF));
  }

  public clearUploadedFiles(): void {
    this.binaryFileService.clearUploadedFiles(KOMMENTAR_UPLOADED_ATTACHMENTS);
  }

  public getCurrentlyEdited(): Observable<ResourceUri> {
    return this._currentlyEdited$.asObservable();
  }

  public setCurrentlyEdited(resourceUri: ResourceUri): void {
    this.clearUploadedFiles();
    if (isNotEmpty(resourceUri)) {
      this.hideNewKommentarFormular();
    }
    this._currentlyEdited$.next(resourceUri);
  }
}
